import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, Flag, Clock, Navigation, MapPin, AlertTriangle, ArrowLeftRight, XCircle, MinusCircle, Store, Pill, Newspaper, Home as HomeIcon } from "lucide-react";
import { psGetTrack, psGetSubstitutionSummary } from "../../lib/psApi";
import { usePsStore } from "../../store/psStore";
import { LiveMap } from "../../components/LiveMap";
import { fmtTime } from "../../lib/format";

const STOP_ICON = { MIGROS: Store, COOP: Store, ALDI: Store, DENNER: Store, PHARMACY: Pill, KIOSK: Newspaper, DELIVERY: HomeIcon };

export default function PsTracking() {
  const { token: paramToken } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { order, refresh } = usePsStore();
  const token = paramToken || order?.track_token;
  const orderNumber = sp.get("n") || order?.order_number || "";

  const [track, setTrack] = useState(null);
  const [summary, setSummary] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { if (!paramToken) refresh().catch(() => {}); }, [paramToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const startPolling = useCallback((tok) => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const d = await psGetTrack(tok);
        setTrack(d);
        if (d.finished && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } catch { /* ignore */ }
    }, 4000);
  }, []);

  const connectWs = useCallback((tok) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    try {
      const ws = new WebSocket(`${base}/api/ps/track/ws/${tok}`);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (ev) => { try { const m = JSON.parse(ev.data); if (m?.type === "track") setTrack(m); } catch { /* ignore */ } };
      ws.onclose = () => { setConnected(false); startPolling(tok); };
      ws.onerror = () => setConnected(false);
    } catch { startPolling(tok); }
  }, [startPolling]);

  useEffect(() => {
    if (!token) return;
    (async () => { try { setTrack(await psGetTrack(token)); } catch { /* ignore */ } })();
    connectWs(token);
    return () => {
      try { wsRef.current?.close(); } catch { /* ignore */ }
      wsRef.current = null;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [token, connectWs]);

  useEffect(() => {
    const oid = order?.id || track?.order_id;
    if (!oid) return;
    psGetSubstitutionSummary(oid).then(setSummary).catch(() => {});
  }, [order?.id, track?.order_id, track?.substitution_pending]);

  const stops = track?.stops || [];
  const active = !!track?.active;
  const finished = !!track?.finished;
  const delivered = track?.status === "delivered";
  const stale = track?.driver && (track.driver.freshness === "STALE" || track.driver.freshness === "OFFLINE");
  const showMap = active && !!track?.driver && !!track?.dropoff;
  const subPending = track?.substitution_pending ?? 0;
  const etaTxt = fmtTime(track?.eta);

  if (!token) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" style={{ color: "#64748b" }}>
        <Navigation size={48} className="mx-auto mb-3" style={{ color: "#cbd5e1" }} />
        <p className="font-semibold">Aucune commande en cours.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => nav(-1)} data-testid="ps-track-back" style={{ color: "#0b1220" }}><ArrowLeft size={24} /></button>
        <h1 className="text-xl font-extrabold" style={{ color: "#0b1220" }}>Suivi de ma commande</h1>
      </div>

      <p className="text-xl font-extrabold mb-3" style={{ color: "#0b1220" }}>Commande #{orderNumber}</p>

      {subPending > 0 && (
        <div className="flex items-center gap-2.5 rounded-xl p-3.5 mb-3" style={{ background: "#fef2f2", border: "1px solid #fecaca" }} data-testid="ps-tracking-subs">
          <ArrowLeftRight size={20} color="#b91c1c" /><span className="flex-1 text-sm font-extrabold" style={{ color: "#b91c1c" }}>{subPending} article{subPending > 1 ? "s nécessitent" : " nécessite"} votre réponse</span>
        </div>
      )}

      <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full mb-3" style={{ background: "#fffbeb" }}>
        {delivered ? <CheckCircle size={18} color="#16a34a" /> : finished ? <Flag size={18} color="#64748b" /> : <Clock size={18} color="#b45309" />}
        <span className="text-sm font-bold" style={{ color: "#92400e" }} data-testid="ps-track-status">{track?.status_label || "Organisation de vos courses"}</span>
      </div>

      {showMap && (
        <div className="mb-3" data-testid="ps-track-live">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: connected ? "#22c55e" : "#f59e0b" }} />
            <span className="text-sm font-bold" style={{ color: "#166534" }}>{connected ? "Position en direct" : "Suivi en cours"}</span>
          </div>
          <LiveMap driver={track.driver} dropoff={track.dropoff} />
          {stale && (
            <div className="flex items-center gap-2 rounded-xl p-3 mt-2" style={{ background: "#fffbeb", border: "1px solid #f59e0b" }} data-testid="ps-track-stale">
              <AlertTriangle size={16} color="#b45309" /><span className="text-xs" style={{ color: "#92400e" }}>Position momentanément indisponible — dernière position connue affichée.</span>
            </div>
          )}
        </div>
      )}

      {active && !track?.driver && (
        <div className="flex items-center gap-2 rounded-xl p-3 mb-2" style={{ background: "#e0f2fe" }} data-testid="ps-track-nogps">
          <MapPin size={16} color="#0369a1" /><span className="text-xs" style={{ color: "#075985" }}>Position momentanément indisponible.</span>
        </div>
      )}

      {active && etaTxt && (
        <div className="flex items-center gap-2 rounded-xl p-3 mb-2" style={{ background: "#dcfce7" }} data-testid="ps-track-eta">
          <Navigation size={16} color="#166534" /><span className="text-sm font-bold" style={{ color: "#166534" }}>Livraison estimée vers {etaTxt}</span>
        </div>
      )}

      {finished && (
        <div className="flex items-center gap-2 rounded-xl p-3 mb-2" style={{ background: "#e0f2fe" }}>
          <Flag size={16} color="#64748b" /><span className="text-xs" style={{ color: "#075985" }}>Tournée terminée.</span>
        </div>
      )}

      {summary && summary.counts.replaced + summary.counts.refused + summary.counts.absent > 0 && (
        <>
          <p className="text-xs font-extrabold tracking-widest mt-6 mb-3" style={{ color: "#64748b" }}>REMPLACEMENTS</p>
          {summary.replaced.map((x, i) => <div key={`r${i}`} className="flex items-center gap-2 bg-white rounded-lg p-3 mb-1.5 text-sm" style={{ border: "1px solid #f0f0f0", color: "#475569" }}><ArrowLeftRight size={15} color="#16a34a" /><span><b>{x.original_name}</b> → {x.replacement_name}</span></div>)}
          {summary.refused.map((x, i) => <div key={`x${i}`} className="flex items-center gap-2 bg-white rounded-lg p-3 mb-1.5 text-sm" style={{ border: "1px solid #f0f0f0", color: "#475569" }}><XCircle size={15} color="#b91c1c" /><span><b>{x.original_name}</b> — remplacement refusé</span></div>)}
          {summary.absent.map((x, i) => <div key={`a${i}`} className="flex items-center gap-2 bg-white rounded-lg p-3 mb-1.5 text-sm" style={{ border: "1px solid #f0f0f0", color: "#475569" }}><MinusCircle size={15} color="#64748b" /><span><b>{x.original_name}</b> — non disponible</span></div>)}
        </>
      )}

      <p className="text-xs font-extrabold tracking-widest mt-6 mb-3" style={{ color: "#64748b" }}>MES ARRÊTS</p>
      {(stops.length > 0 ? stops : (order?.sub_orders || []).map((s) => ({ kind: "PICKUP", label: s.store_name, stop_type: s.stop_type, status: "pending", status_label: "En attente" }))).map((s, i) => {
        const done = s.status === "done";
        const inProg = s.status === "in_progress";
        const Icon = STOP_ICON[s.stop_type] || Store;
        const color = done ? "#16a34a" : inProg ? "#b45309" : "#64748b";
        return (
          <div key={`${s.kind}-${i}`} className="flex items-center gap-3 bg-white rounded-xl p-3.5 mb-2" style={{ border: "1px solid #f0f0f0" }}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: color + "1A" }}><Icon size={20} color={color} /></div>
            <div className="flex-1">
              <p className="font-extrabold text-sm" style={{ color: "#0b1220" }}>{s.label}</p>
              <p className="text-xs mt-0.5" style={{ color: "#64748b" }}>{s.status_label || "En attente"}</p>
            </div>
            {done ? <CheckCircle size={18} color="#16a34a" /> : <span className="w-2.5 h-2.5 rounded-full" style={{ background: inProg ? "#f59e0b" : "#cbd5e1" }} />}
          </div>
        );
      })}
    </div>
  );
}
